import {
  collection,
  doc,
  DocumentData,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  setDoc,
  SnapshotOptions,
  WithFieldValue,
  writeBatch,
} from 'firebase/firestore';
import { firestore } from '@/lib/firebase';
import { TABLET_COLLECTION } from '../schema/tablet';
import {
  TABLET_GROUP_COLLECTION,
  TabletGroup,
} from '../schema/tabletGroup';
import { useCollectionData } from 'react-firebase-hooks/firestore';

// Create or update a group and re-stamp member tablets' groupId in one atomic
// batch, so the public XML endpoint never sees a half-applied membership edit.
export async function saveTabletGroup(
  group: TabletGroup,
  addTabletIds: string[],
  removeTabletIds: string[]
) {
  const groupRef = group.id
    ? doc(firestore, TABLET_GROUP_COLLECTION, group.id)
    : doc(collection(firestore, TABLET_GROUP_COLLECTION));

  const batch = writeBatch(firestore);
  batch.set(
    groupRef,
    { name: group.name.trim(), enabled: group.enabled },
    { merge: true }
  );
  addTabletIds.forEach((tabletId) => {
    batch.update(doc(firestore, TABLET_COLLECTION, tabletId), {
      groupId: groupRef.id,
    });
  });
  removeTabletIds.forEach((tabletId) => {
    batch.update(doc(firestore, TABLET_COLLECTION, tabletId), {
      groupId: null,
    });
  });
  await batch.commit();
}

export async function updateTabletGroup(
  id: string,
  group: Partial<TabletGroup>
) {
  delete group.id;

  await setDoc(doc(firestore, TABLET_GROUP_COLLECTION, id), { ...group }, {
    merge: true,
  });
}

// Master switch: flip every group's enabled flag in one atomic batch.
export async function setAllTabletGroupsEnabled(
  groupIds: string[],
  enabled: boolean
) {
  const batch = writeBatch(firestore);
  groupIds.forEach((id) => {
    batch.update(doc(firestore, TABLET_GROUP_COLLECTION, id), { enabled });
  });
  await batch.commit();
}

// Delete a group and ungroup its member tablets (they report OFF) atomically.
export async function deleteTabletGroup(id: string, memberTabletIds: string[]) {
  const batch = writeBatch(firestore);
  memberTabletIds.forEach((tabletId) => {
    batch.update(doc(firestore, TABLET_COLLECTION, tabletId), {
      groupId: null,
    });
  });
  batch.delete(doc(firestore, TABLET_GROUP_COLLECTION, id));
  await batch.commit();
}

const tabletGroupConverter: FirestoreDataConverter<TabletGroup> = {
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions
  ): TabletGroup {
    const data = snapshot.data(options);
    return {
      ...data,
      id: snapshot.id,
    } as TabletGroup;
  },
  toFirestore: function (): WithFieldValue<DocumentData> {
    throw new Error('Function not implemented.');
  },
};

// list all tablet groups
export function useTabletGroups() {
  return useCollectionData(
    collection(firestore, TABLET_GROUP_COLLECTION).withConverter(
      tabletGroupConverter
    ),
    {
      snapshotListenOptions: { includeMetadataChanges: false },
    }
  );
}
